/**
 * Accounts the analytics screen leaves out.
 *
 * Akhil, 2026-09-22: "hide the aquamaristrading.com in the analysis page". Those two accounts
 * (`support@`, `provision1@`) carry bulk-loaded enquiries — 62 of the 279 on the bench — that
 * describe the legacy system rather than anyone's sourcing behaviour, and they sit at the top of
 * the purchaser board reading like real numbers.
 *
 * Excluded at the FACTS, once, so every board and the filter list agree: hiding a purchaser from
 * one table while the KPIs above it still count them is worse than not hiding at all. Nothing is
 * deleted and no other screen is affected — the enquiries are still theirs everywhere else.
 *
 * To change what is hidden, edit the list below. It is data, deliberately: the next account to
 * exclude should be one line here, not a condition somewhere in a component.
 */
import type { EnquiryFact } from './types'

/** Whole domains, lowercase, no `@`. */
export const EXCLUDED_PURCHASER_DOMAINS: readonly string[] = ['aquamaristrading.com']

/** Individual addresses, for when a domain is too broad. */
export const EXCLUDED_PURCHASERS: readonly string[] = []

export function isExcludedPurchaser(purchaser: string): boolean {
  const email = purchaser.trim().toLowerCase()
  if (email === '') return false
  if (EXCLUDED_PURCHASERS.includes(email)) return true
  const at = email.lastIndexOf('@')
  // The DOMAIN, not a substring: `aquamaristrading.com@gmail.com` is a gmail address.
  return at >= 0 && EXCLUDED_PURCHASER_DOMAINS.includes(email.slice(at + 1))
}

export function withoutExcludedPurchasers<T extends Pick<EnquiryFact, 'purchaser'>>(facts: readonly T[]): T[] {
  return facts.filter((f) => !isExcludedPurchaser(f.purchaser))
}

/** What the screen says it is leaving out, or `''` when it is leaving out nothing. */
export function describeExcluded(): string {
  const names = [...EXCLUDED_PURCHASER_DOMAINS, ...EXCLUDED_PURCHASERS]
  return names.length === 0 ? '' : names.join(', ')
}
